import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type UserRole = 'admin' | 'editor' | 'user' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isEditor: boolean;
  userRole: UserRole;
  isLoading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const checkUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error checking user role:", error);
        return { isAdmin: false, isEditor: false, role: null as UserRole };
      }

      if (data) {
        const role = data.role as UserRole;
        return {
          isAdmin: role === 'admin',
          isEditor: role === 'editor',
          role
        };
      }

      return { isAdmin: false, isEditor: false, role: 'user' as UserRole };
    } catch (error) {
      console.error("Error checking user role:", error);
      return { isAdmin: false, isEditor: false, role: null as UserRole };
    }
  };

  const handleSessionExpired = useCallback(() => {
    setSession(null);
    setUser(null);
    setIsAdmin(false);
    setIsEditor(false);
    setUserRole(null);
    
    toast({
      title: "Session expired",
      description: "Please sign in again to continue.",
      variant: "destructive",
    });
    
    // Redirect to auth page after a brief delay
    setTimeout(() => {
      if (window.location.pathname !== "/auth") {
        window.location.href = "/auth";
      }
    }, 1000);
  }, [toast]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Handle token refresh errors - clear stale session and redirect
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn("Token refresh failed, clearing session");
          handleSessionExpired();
          setIsLoading(false);
          return;
        }

        // Handle sign out event
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setIsEditor(false);
          setUserRole(null);
          setIsLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Defer role check with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            checkUserRole(session.user.id).then(({ isAdmin, isEditor, role }) => {
              setIsAdmin(isAdmin);
              setIsEditor(isEditor);
              setUserRole(role);
            });
          }, 0);
        } else {
          setIsAdmin(false);
          setIsEditor(false);
          setUserRole(null);
        }

        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        checkUserRole(session.user.id).then(({ isAdmin, isEditor, role }) => {
          setIsAdmin(isAdmin);
          setIsEditor(isEditor);
          setUserRole(role);
        });
      }

      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [handleSessionExpired]);

  // Clear session on browser close if "Remember me" was unchecked
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionStorage.getItem('tempSession') === 'true') {
        supabase.auth.signOut();
        sessionStorage.removeItem('tempSession');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    // When rememberMe is false, clear any existing session first
    // and the session will only last until the browser is closed
    if (!rememberMe) {
      // Sign out first to clear persistent session
      await supabase.auth.signOut();
    }
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // If not remembering, store a flag to clear session on browser close
    if (!rememberMe && !error) {
      sessionStorage.setItem('tempSession', 'true');
    } else {
      sessionStorage.removeItem('tempSession');
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || "",
        },
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setIsEditor(false);
    setUserRole(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, session, isAdmin, isEditor, userRole, isLoading, signIn, signUp, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
